<!--
  NOTE: This component uses a placeholder for the @/components/Partner/PartnerPaymentRequestsTable component.
  The real one will be built with @/components/GraphqlDataTable/GraphqlDataTable.vue

  This is setup with some fake static data that was used to build the placeholder.
  Of course disregard everything about this data since actual data will be fetched from the Public API.
-->
<template>
  <PageHeader>
    <template #header-title>Payment requests</template>
    <template #header-description>Choose workers to submit to this job</template>
  </PageHeader>

  <WdsTabs class="tw-mb-4">
    <WdsTab :tab-key="'to-be-payrolled'" title="To be payrolled">
      <template #title>
        <WdsStack align-center gap-md>
          {{ $t('To be payrolled') }}
          <WdsLabel v-if="toBePayrolledRequests.length > 0" xs>{{ toBePayrolledRequests.length }}</WdsLabel>
        </WdsStack>
      </template>
      <div class="tw-mt-2">
        <PartnerPaymentRequestsTable :payment-requests="toBePayrolledRequests" />
      </div>
    </WdsTab>
    <WdsTab :tab-key="'processing'" title="Processing">
      <template #title>
        <WdsStack align-center gap-md>
          {{ $t('Processing') }}
          <WdsLabel v-if="processingRequests.length > 0" xs>{{ processingRequests.length }}</WdsLabel>
        </WdsStack>
      </template>
      <div class="tw-mt-2">
        <PartnerPaymentRequestsTable :payment-requests="processingRequests" />
      </div>
    </WdsTab>
    <WdsTab :tab-key="'payrolled'" title="Payrolled">
      <template #title>
        <WdsStack align-center gap-md>
          {{ $t('Payrolled') }}
          <WdsLabel v-if="payrolledRequests.length > 0" xs>{{ payrolledRequests.length }}</WdsLabel>
        </WdsStack>
      </template>
      <div class="tw-mt-2">
        <PartnerPaymentRequestsTable :payment-requests="payrolledRequests" />
      </div>
    </WdsTab>
  </WdsTabs>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { WdsTabs, WdsTab, WdsStack, WdsLabel } from '@worksome/wds-components'
import PageHeader from '@/components/PageHeader.vue'
import PartnerPaymentRequestsTable from './PartnerPaymentRequestsTable.vue'
import { $t } from '@/composables/translation'

// Fake data for payment requests tables based on the screenshot
const toBePayrolledRequests = ref([
  {
    id: '1',
    clientApprovalDate: '2025-03-17',
    prId: '#51456',
    worker: {
      name: 'Ayesha Mortensen',
      freelancerId: '123455'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 35.75,
    payRate: {
      amount: 134,
      type: 'hourly'
    },
    expenses: 2345,
    jobTitle: 'Senior Front-End Developer #2',
    jobId: '69263541-1762',
    clientPaymentStatus: 'Paid' as const,
    assignmentCompany: 'Apple Inc.',
    workLocation: '1234 Maple Avenue, Springfield, IL'
  },
  {
    id: '2',
    clientApprovalDate: '2025-03-17',
    prId: '#51455',
    worker: {
      name: 'Ben Tullet',
      freelancerId: '72345'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 35.75,
    payRate: {
      amount: 134,
      type: 'hourly'
    },
    expenses: null,
    jobTitle: 'Senior Front-End Developer #2',
    jobId: '69263541-1762',
    clientPaymentStatus: 'Not paid' as const,
    assignmentCompany: 'Apple Inc.',
    workLocation: '6789 Cedar Drive, Greenfield, NY'
  },
  {
    id: '3',
    clientApprovalDate: '2025-03-17',
    prId: '#51454',
    worker: {
      name: 'Annette Carax',
      freelancerId: '238766'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 4,
    payRate: {
      amount: 5335,
      type: 'weekly'
    },
    expenses: 8334,
    jobTitle: 'Senior Front-End Developer #2',
    jobId: '69263541-1762',
    clientPaymentStatus: 'Not paid' as const,
    assignmentCompany: 'Apple Inc.',
    workLocation: '4680 Redwood Avenue, Crestview, OR'
  },
  {
    id: '4',
    clientApprovalDate: '2025-03-17',
    prId: '#51453',
    worker: {
      name: 'Kasper Niklas Kjær',
      freelancerId: '8635415'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 35.75,
    payRate: {
      amount: 134,
      type: 'hourly'
    },
    expenses: 354,
    jobTitle: 'Senior Front-End Developer #2',
    jobId: '69263541-1762',
    clientPaymentStatus: 'Not paid' as const,
    assignmentCompany: 'Apple Inc.',
    workLocation: '8901 Pine Lane, Hilltop, CA'
  },
  {
    id: '5',
    clientApprovalDate: '2025-03-17',
    prId: '#51452',
    worker: {
      name: 'Anders Hansen',
      freelancerId: '963242'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 4,
    payRate: {
      amount: 5335,
      type: 'weekly'
    },
    expenses: null,
    jobTitle: 'Full-stack engineer',
    jobId: '69263541-1762',
    clientPaymentStatus: 'Not paid' as const,
    assignmentCompany: 'FinTech Innovations',
    workLocation: '7890 Spruce Way, Meadowbrook, OH'
  },
  {
    id: '6',
    clientApprovalDate: '2025-03-17',
    prId: '#51451',
    worker: {
      name: 'Mark Wedell',
      freelancerId: '632853'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 3,
    payRate: {
      amount: 5335,
      type: 'weekly'
    },
    expenses: 3544,
    jobTitle: 'Product Manager',
    jobId: '69263541-1762',
    clientPaymentStatus: 'Paid' as const,
    assignmentCompany: 'FinTech Innovations',
    workLocation: '2468 Chestnut Path, Riverdale, CO'
  },
  {
    id: '7',
    clientApprovalDate: '2025-03-17',
    prId: '#51450',
    worker: {
      name: 'Frederikke Mitchell',
      freelancerId: '563685'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 3.5,
    payRate: {
      amount: 5335,
      type: 'weekly'
    },
    expenses: null,
    jobTitle: 'Full-stack engineer',
    jobId: '69263541-1762',
    clientPaymentStatus: 'Paid' as const,
    assignmentCompany: 'Pitch Perfect Solutions',
    workLocation: '6802 Palm Drive, Oceanview, NJ'
  },
  {
    id: '8',
    clientApprovalDate: '2025-03-17',
    prId: '#51449',
    worker: {
      name: 'Sophia Taylor',
      freelancerId: '738429'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 4,
    payRate: {
      amount: 5335,
      type: 'weekly'
    },
    expenses: null,
    jobTitle: 'Full-stack engineer',
    jobId: '69263541-1762',
    clientPaymentStatus: 'Paid' as const,
    assignmentCompany: 'Pitch Perfect Solutions',
    workLocation: '6802 Palm Drive, Oceanview, NJ'
  },
  {
    id: '9',
    clientApprovalDate: '2025-03-17',
    prId: '#51448',
    worker: {
      name: 'Thomas Clark',
      freelancerId: '892137'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 4,
    payRate: {
      amount: 5335,
      type: 'weekly'
    },
    expenses: null,
    jobTitle: 'Full-stack engineer',
    jobId: '69263541-1762',
    clientPaymentStatus: 'Paid' as const,
    assignmentCompany: 'Pitch Perfect Solutions',
    workLocation: '6802 Palm Drive, Oceanview, NJ'
  }
]);

const processingRequests = ref([
  {
    id: '10',
    clientApprovalDate: '2025-03-17',
    prId: '#51447',
    worker: {
      name: 'Elena Johnson',
      freelancerId: '512893'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 35.75,
    payRate: {
      amount: 134,
      type: 'hourly'
    },
    expenses: 2100,
    jobTitle: 'UI Designer',
    jobId: '69263541-1763',
    clientPaymentStatus: 'Paid' as const,
    assignmentCompany: 'Creative Design Co',
    workLocation: '3456 Oak Street, Riverside, CA'
  },
  {
    id: '11',
    clientApprovalDate: '2025-03-17',
    prId: '#51446',
    worker: {
      name: 'Marcus Williams',
      freelancerId: '673291'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 40,
    payRate: {
      amount: 145,
      type: 'hourly'
    },
    expenses: 1500,
    jobTitle: 'React Developer',
    jobId: '69263541-1764',
    clientPaymentStatus: 'Paid' as const,
    assignmentCompany: 'WebTech Solutions',
    workLocation: '7890 Elm Boulevard, Lakeside, MI'
  }
]);

const payrolledRequests = ref([
  {
    id: '12',
    clientApprovalDate: '2025-03-17',
    prId: '#51445',
    worker: {
      name: 'Jessica Miller',
      freelancerId: '429381'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 35,
    payRate: {
      amount: 5500,
      type: 'weekly'
    },
    expenses: 4200,
    jobTitle: 'Project Manager',
    jobId: '69263541-1765',
    clientPaymentStatus: 'Paid' as const,
    assignmentCompany: 'Management Experts Inc',
    workLocation: '5678 Birch Court, Hillside, TX'
  },
  {
    id: '13',
    clientApprovalDate: '2025-03-17',
    prId: '#51444',
    worker: {
      name: 'Daniel Robertson',
      freelancerId: '583921'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 3.5,
    payRate: {
      amount: 5200,
      type: 'weekly'
    },
    expenses: null,
    jobTitle: 'DevOps Engineer',
    jobId: '69263541-1766',
    clientPaymentStatus: 'Paid' as const,
    assignmentCompany: 'Cloud Systems Ltd',
    workLocation: '1357 Maple Street, Mountain View, CA'
  },
  {
    id: '14',
    clientApprovalDate: '2025-03-17',
    prId: '#51443',
    worker: {
      name: 'Olivia Chen',
      freelancerId: '982143'
    },
    timesheetPeriod: '2025-03-17 - 2025-03-23',
    units: 4,
    payRate: {
      amount: 5400,
      type: 'weekly'
    },
    expenses: 3200,
    jobTitle: 'UX Researcher',
    jobId: '69263541-1767',
    clientPaymentStatus: 'Paid' as const,
    assignmentCompany: 'User Experience Lab',
    workLocation: '2468 Walnut Drive, Eastwood, NY'
  }
]);
</script>
